import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { router } from '@inertiajs/react'
import { Plus, Trash2, Clock, Users, Calendar, CheckCircle2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface AvailabilitySlot {
  id: number
  day_of_week: number
  day_name: string
  start_time: string
  end_time: string
  max_capacity: number
  teacher_name?: string
  occupied_count: number
  has_capacity: boolean
  available: boolean
}

interface Teacher {
  id: number
  name: string
}

interface School {
  id: number
  name: string
}

interface AvailabilitySlotsIndexProps {
  slots_by_day: Record<string, AvailabilitySlot[]>
  teachers: Teacher[]
  school: School
}

export default function AvailabilitySlotsIndex({ slots_by_day, teachers, school }: AvailabilitySlotsIndexProps) {
  const today = new Date()
  const [selectedDay, setSelectedDay] = useState<number>(today.getDay())
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [newSlot, setNewSlot] = useState({
    day_of_week: today.getDay(),
    start_time: '08:00',
    end_time: '09:00',
    teacher_id: '',
    max_capacity: 1
  })

  // Calcula a data do dia selecionado na semana atual
  const getDateForDay = (dayOfWeek: number) => {
    const current = new Date()
    const currentDay = current.getDay()
    const diff = dayOfWeek - currentDay
    const targetDate = new Date(current)
    targetDate.setDate(current.getDate() + diff)
    return targetDate
  }

  const formatDate = (date: Date) => {
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
    return `${date.getDate().toString().padStart(2, '0')} de ${months[date.getMonth()]}`
  }

  const daysOfWeek = [
    { value: 0, label: 'Domingo', short: 'Dom' },
    { value: 1, label: 'Segunda', short: 'Seg' },
    { value: 2, label: 'Terça', short: 'Ter' },
    { value: 3, label: 'Quarta', short: 'Qua' },
    { value: 4, label: 'Quinta', short: 'Qui' },
    { value: 5, label: 'Sexta', short: 'Sex' },
    { value: 6, label: 'Sábado', short: 'Sáb' },
  ]

  const handleSubmit = () => {
    router.post('/availability_slots', {
      availability_slot: newSlot
    }, {
      onSuccess: () => {
        setIsDialogOpen(false)
        setNewSlot({
          day_of_week: selectedDay,
          start_time: '08:00',
          end_time: '09:00',
          teacher_id: '',
          max_capacity: 1
        })
      }
    })
  }

  const handleDelete = () => {
    if (deleteId) {
      router.delete(`/availability_slots/${deleteId}`, {
        onSuccess: () => setDeleteId(null)
      })
    }
  }

  const selectedSlots = (slots_by_day[selectedDay.toString()] || []).sort((a, b) => 
    a.start_time.localeCompare(b.start_time)
  )

  const timeSlots = Array.from({ length: 14 }, (_, i) => {
    const hour = 6 + i
    return `${hour.toString().padStart(2, '0')}:00`
  })

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold">Horários Disponíveis</h2>
            <p className="text-muted-foreground mt-1">
              Configure os horários que sua escola oferece para aulas
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Novo Horário
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Adicionar Horário Disponível</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Dia da Semana</Label>
                  <Select
                    value={newSlot.day_of_week.toString()}
                    onValueChange={(value) => setNewSlot({ ...newSlot, day_of_week: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {daysOfWeek.map((day) => (
                        <SelectItem key={day.value} value={day.value.toString()}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora Início</Label>
                    <Select
                      value={newSlot.start_time}
                      onValueChange={(value) => {
                        const [hour] = value.split(':')
                        const endHour = (parseInt(hour) + 1).toString().padStart(2, '0')
                        setNewSlot({ 
                          ...newSlot, 
                          start_time: value,
                          end_time: `${endHour}:00`
                        })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Hora Fim</Label>
                    <input
                      type="text"
                      value={newSlot.end_time}
                      disabled
                      className="w-full px-3 py-2 rounded-md border bg-muted"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Professor (opcional)</Label>
                  <Select
                    value={newSlot.teacher_id}
                    onValueChange={(value) => setNewSlot({ ...newSlot, teacher_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Qualquer professor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Qualquer professor</SelectItem>
                      {teachers.map((teacher) => (
                        <SelectItem key={teacher.id} value={teacher.id.toString()}>
                          {teacher.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Capacidade Máxima</Label>
                  <Select
                    value={newSlot.max_capacity.toString()}
                    onValueChange={(value) => setNewSlot({ ...newSlot, max_capacity: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 8, 10].map((capacity) => (
                        <SelectItem key={capacity} value={capacity.toString()}>
                          {capacity} aluno{capacity > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit}>
                  Adicionar Horário
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Seletor de Dias */}
        <Card className="border-none shadow-none bg-transparent">
          <CardContent className="p-0">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {daysOfWeek.map((day) => {
                const slotsCount = slots_by_day[day.value.toString()]?.length || 0
                const isSelected = selectedDay === day.value
                const isToday = day.value === today.getDay()
                const dayDate = getDateForDay(day.value)

                return (
                  <button
                    key={day.value}
                    onClick={() => setSelectedDay(day.value)}
                    className={`flex-shrink-0 min-w-[110px] transition-all`}
                  >
                    <Card className={`border-2 transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/30' 
                        : isToday
                        ? 'border-primary/30 bg-accent/30'
                        : 'border-border hover:bg-accent/50'
                    }`}>
                      <CardContent className="p-4">
                        <div className={`font-semibold text-sm ${
                          isSelected ? 'text-blue-700 dark:text-blue-300' : ''
                        }`}>
                          {day.label}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatDate(dayDate)}
                        </div>
                        {isToday && (
                          <Badge variant="outline" className="text-[10px] mt-2 py-0">
                            Hoje
                          </Badge>
                        )}
                        {slotsCount > 0 && (
                          <div className="text-xs text-muted-foreground mt-2">
                            {slotsCount} horário{slotsCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Grade de Horários */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{daysOfWeek[selectedDay].label} - {formatDate(getDateForDay(selectedDay))}</span>
              <Badge variant="secondary">
                {selectedSlots.length} horário{selectedSlots.length !== 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedSlots.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum horário configurado para este dia</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setNewSlot({ ...newSlot, day_of_week: selectedDay })
                    setIsDialogOpen(true)
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Horário
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedSlots.map((slot) => (
                  <div
                    key={slot.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary" />
                        <span className="font-bold text-lg">
                          {slot.start_time} - {slot.end_time}
                        </span>
                      </div>
                      
                      {slot.teacher_name && (
                        <Badge variant="outline">
                          {slot.teacher_name}
                        </Badge>
                      )}
                      
                      <div className="flex items-center gap-1 text-sm">
                        <Users className="h-4 w-4" />
                        <span>{slot.occupied_count}/{slot.max_capacity}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {slot.has_capacity ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Disponível
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Lotado
                        </Badge>
                      )}
                      
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteId(slot.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog de Confirmação de Exclusão */}
        <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja remover este horário disponível? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>
                Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}
